import { useMemo } from "react";

import { SwitchPlain } from "~/components/formElements/Switch/Switch.tsx";
import type {
	StaffingUnlock,
	UpdateStaffingUnlocksPayload,
} from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";

type UserStaffingUnlocks = {
	userId: string;
	userName: string;
	staffingUnlocks: StaffingUnlock[];
};

type Props = {
	onChange: (payload: UpdateStaffingUnlocksPayload) => void;
	staffingUnlocks: StaffingUnlock[];
};

function groupByUser(staffingUnlocks: StaffingUnlock[]): UserStaffingUnlocks[] {
	const groups = new Map<string, UserStaffingUnlocks>();
	for (const staffingUnlock of staffingUnlocks) {
		const group = groups.get(staffingUnlock.userId)
			?? { userId: staffingUnlock.userId, userName: staffingUnlock.userName, staffingUnlocks: [] };
		group.staffingUnlocks.push(staffingUnlock);
		groups.set(staffingUnlock.userId, group);
	}
	return [...groups.values()].sort((a, b) => a.userName.localeCompare(b.userName));
}

export function StaffingUnlocksPanel({ onChange, staffingUnlocks }: Props) {
	const userGroups = useMemo(() => groupByUser(staffingUnlocks), [staffingUnlocks]);
	const allUnlocked = staffingUnlocks.every(staffingUnlock => staffingUnlock.unlocked);

	const changeStaffings = (staffingIds: string[], unlocked: boolean) => {
		onChange({ unlocked, scope: "staffings", staffingIds });
	};

	return <div className="space-y-2 text-sm">
		<SwitchPlain checked={allUnlocked}
					 inputSize="sm"
					 label="Alle Staffings entsperrt"
					 onChange={(unlocked) => onChange({ unlocked, scope: "all" })} />
		<ul className="space-y-2 pl-4">
			{userGroups.map(userGroup => {
				const userAllUnlocked = userGroup.staffingUnlocks.every(staffingUnlock => staffingUnlock.unlocked);
				const userStaffingIds = userGroup.staffingUnlocks.map(staffingUnlock => staffingUnlock.staffingId);

				return <li key={userGroup.userId}
						   className="space-y-1">
					<SwitchPlain checked={userAllUnlocked}
								 inputSize="sm"
								 label={userGroup.userName}
								 onChange={(unlocked) => changeStaffings(userStaffingIds, unlocked)} />
					{/* With one staffing the switch of the user is the switch of the staffing. */}
					{userGroup.staffingUnlocks.length > 1 && <ul className="space-y-1 pl-4">
						{userGroup.staffingUnlocks.map(staffingUnlock =>
							<li key={staffingUnlock.staffingId}>
								<SwitchPlain checked={staffingUnlock.unlocked}
											 inputSize="sm"
											 label={staffingUnlock.orderName}
											 onChange={(unlocked) => changeStaffings([staffingUnlock.staffingId], unlocked)} />
							</li>)}
					</ul>}
				</li>;
			})}
		</ul>
	</div>;
}
